import { ActivityV2 } from 'models/activityV2'
import { BaseEventCard } from './BaseEventCard'

interface DocumentEventCardProps {
  event: ActivityV2
}

export function DocumentEventCard({ event }: DocumentEventCardProps) {
  const { action_type } = event

  // Get document info from field_history_objects (includes file URL from backend)
  // For tag updates, field_name will be 'tags', for name/delete it will be 'name'
  const documentField = event.field_history_objects?.find(
    (field) => field.field_name === 'name' || field.field_name === 'tags'
  )

  // Check if this is a company association change
  // The field_name in field_history_objects is 'company_association'
  const companyAssociationField = event.field_history_objects?.find(
    (field) => field.field_name === 'company_association'
  )

  // Get contact_company name for CREATE events (from any field in field_history_objects)
  // During CREATE, all field_history_objects will have the same contact_company_name
  const contactCompanyName =
    action_type === 'CREATE'
      ? event.field_history_objects?.[0]?.contact_company_name
      : undefined

  // document_name is sent for association-only updates where there is no name/tags field
  const documentName =
    documentField?.value ||
    documentField?.previous_value ||
    event.field_history_objects?.find((field) => field.document_name)
      ?.document_name
  const previousName = documentField?.previous_value
  const documentFile =
    documentField?.document_file ||
    event.field_history_objects?.find((field) => field.document_file)
      ?.document_file
  const isReplacement =
    documentField?.document_is_replacement ||
    event.field_history_objects?.find((field) => field.document_is_replacement)
      ?.document_is_replacement

  // If document name is null/undefined, it means the document was deleted
  const isDocumentDeleted = !documentName
  const displayName = isDocumentDeleted
    ? 'deleted document'
    : action_type === 'DELETE'
      ? previousName || documentName
      : documentName

  // Build action text based on action type
  const getActionText = () => {
    switch (action_type) {
      case 'CREATE': {
        const baseAction = isReplacement
          ? 'replaced a document'
          : 'uploaded a document'
        // Include company name if document was uploaded with a company association
        return contactCompanyName
          ? `${baseAction} to ${contactCompanyName}`
          : baseAction
      }
      case 'UPDATE': {
        // Check if this is a company association change
        if (companyAssociationField) {
          const newCompanyName = companyAssociationField.contact_company_name
          if (newCompanyName) {
            return `changed company association to ${newCompanyName}`
          }
        }
        return isDocumentDeleted ? 'updated a' : 'updated a document'
      }
      case 'DELETE':
        return 'deleted a document'
      default:
        return 'modified a document'
    }
  }

  const actionText = getActionText()

  // Handle document name click
  const handleDocumentClick = () => {
    if (documentFile) {
      // Backend now provides full S3 URLs, so use directly
      window.open(documentFile, '_blank')
    }
  }

  return (
    <BaseEventCard event={event}>
      <div className="space-y-1">
        {/* User name and action */}
        <div className="font-inter text-sm leading-[1.43] text-gray-500">
          <span className="text-gray-700 font-bold">{event.user_name}</span>{' '}
          {actionText}
        </div>

        {/* Document name - always show as clickable link if file is available */}
        <div className="font-inter text-sm leading-[1.43] font-medium text-gray-700">
          {documentFile && action_type !== 'DELETE' ? (
            <button
              onClick={handleDocumentClick}
              className="text-left hover:underline cursor-pointer transition-all"
              title="Click to view document"
            >
              {displayName}
            </button>
          ) : (
            <span>{displayName}</span>
          )}
        </div>
      </div>
    </BaseEventCard>
  )
}
